import { ApolloError, useLazyQuery } from '@apollo/client'
import { MeQuery, MeQueryVariables } from 'src/generated'

import { useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { authSlice } from '../../../../redux/slices'
import ME_QUERY from './meQuery.gql'

type ReturnType = [
  () => Promise<MeQuery['me']['user'] | null>,
  {
    data?: MeQuery
    loading: boolean
    error?: ApolloError
  },
]

const TTL_MS = 2.5 * 60 * 1000 // 2.5 minutes

export const useMeLazyQuery = (): ReturnType => {
  const [query, { data, loading, error }] = useLazyQuery<
    MeQuery,
    MeQueryVariables
  >(ME_QUERY, {
    // Authorisation, and already cached below with a TTL of its own. A second
    // cache in Apollo would expire on different terms from the one the boot
    // path actually reads.
    fetchPolicy: 'no-cache',
  })

  const dispatch = useDispatch()
  const cachedUserRef = useRef<MeQuery['me']['user'] | null>(null)
  const cacheTimestampRef = useRef<number | null>(null)

  const getMe = useCallback(async () => {
    const now = Date.now()

    // Use cached value if it's still valid
    if (
      cachedUserRef.current &&
      cacheTimestampRef.current &&
      now - cacheTimestampRef.current < TTL_MS
    ) {
      return cachedUserRef.current
    }

    const { data } = await query()

    const userData = data?.me.user ?? null

    // Store in ref cache
    cachedUserRef.current = userData
    cacheTimestampRef.current = now

    dispatch(authSlice.actions.setAdmin(!!userData?.isAdmin))

    return userData
  }, [query, dispatch])

  return [
    getMe,
    {
      data,
      loading,
      error,
    },
  ]
}
